import React, { useEffect, useState } from "react";
import { 
  Box, 
  Button, 
  Typography, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField 
} from "@mui/material";
import InfoComponent from "../components/InfoComponent";
import { currentSelectedLanguage } from "../features/selectedLanguage/languageSlice";
import { useDispatch, useSelector } from "react-redux";
import { fetchPanelList } from "../features/panels/panelsSlice";
import {useParams } from "react-router-dom";
import Loader from "../components/Loader";
import {
  fetchcomicTitle,
  selectComicSubTitle,
  selectComicTitle,
  selectComicTitleStatus,
} from "../features/comicTitle/comicTitleSlice";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadPost } from "../api/downloadPost";
import { submitToCMS } from "../api/submitToCMS";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import CanvasPanels from "../components/CanvasPanels";
import LanguageSelector from "../components/LanguageSelector";

const CanvasDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isUpdated, setIsUpdated] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isUpdatedComicTitle, setIsUpdatedComicTitle] = useState(false);
  const [openComicIdDialog, setOpenComicIdDialog] = useState(false);
  const [comicId, setComicId] = useState("");
  const [episodeNumber, setEpisodeNumber] = useState("");
  const lang = useSelector(currentSelectedLanguage);
  const panelStatus = useSelector((state) => state.panels.status);
  const comicTitleStatus = useSelector(selectComicTitleStatus);
  const comicSubTitle = useSelector(selectComicSubTitle);
  const comicTitle = useSelector(selectComicTitle);
  
  const handleOpenComicIdDialog = () => {
    setOpenComicIdDialog(true);
  };

  const handleCloseComicIdDialog = () => {
    setOpenComicIdDialog(false);
    setComicId("");
    setEpisodeNumber("");
  };

  const HandleSubmitClick = async () => {
    // submit to cms
    console.log("Submitting to CMS...", id, lang, comicId, episodeNumber);
    const formData = new FormData();
    formData.append('comic_input_file_id', id);
    formData.append('language', lang);
    formData.append('comic_id', comicId);
    formData.append('episode_number', episodeNumber);

    try {
      const response = await submitToCMS(formData);

      console.log("Response from CMS:", response);

      if (response.success) {
        alert("Data sent successfully to CMS!");
        handleCloseComicIdDialog();
      } else {
        alert("Failed to export data: " + response.error);
      }
    }
    catch (error) {
      console.error("Error saving image:", error);
      alert("Failed to export data. Please try again.");
    }
  };

  const handleComicIdSubmit = () => {
    if (!comicId.trim()) {
      alert("Please enter a valid Comic ID");
      return;
    }
    if (!episodeNumber.trim()) {
      alert("Please enter a valid Episode Number");
      return;
    }
    HandleSubmitClick();
  };

  useEffect(() => {
    if (panelStatus !== "loading" && (panelStatus === "idle" || !isUpdated)) {
      dispatch(fetchPanelList(id));
      setIsUpdated(true);
    }
  }, [panelStatus, dispatch, id, isUpdated]);

  useEffect(() => {
    if (
      comicTitleStatus !== "loading" &&
      (comicTitleStatus === "idle" || !isUpdatedComicTitle)
    ) {
      dispatch(fetchcomicTitle(id));
      setIsUpdatedComicTitle(true);
    }
  }, [comicTitleStatus, dispatch, id, isUpdatedComicTitle]);

  if (panelStatus !== "succeeded" && comicTitleStatus !== "succeeded") {
    return <Loader />;
  } else {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h6" mb="3px">
              Canvas
            </Typography>
            <Box sx={{ display: "flex" }}>
              <Typography variant="h4" fontWeight="bold">
                {comicTitle} / {comicSubTitle}
              </Typography>
              <InfoComponent />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <LanguageSelector />
            <Button
              variant="outlined"
              endIcon={<DownloadIcon />}
              size="small"
              sx={{
                padding: "10px",
                borderRadius: "10px",
              }}
              disabled={isDisabled}
              onClick={async () => {
                try {
                  setIsDisabled(true);

                  // Download translation first
                  await downloadPost("translation", id, lang);

                  // Then download extraction
                  await downloadPost("extraction", id);

                } catch (error) {
                  console.error("Download failed:", error);
                } finally {
                  setIsDisabled(false);
                }
              }}
            >
              Download Report
            </Button>
            <Button 
              sx={{
                padding: "10px",
                borderRadius: "10px",
              }}
              onClick={handleOpenComicIdDialog}
            >
              Submit to CMS
            </Button>
          </Box>
        </Box>
        
        {/* Comic ID Dialog */}
        <Dialog 
          open={openComicIdDialog} 
          onClose={handleCloseComicIdDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Enter Comic Details</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              id="comic-id"
              label="Comic ID"
              type="text"
              fullWidth
              variant="outlined"
              value={comicId}
              onChange={(e) => setComicId(e.target.value)}
              placeholder="Enter the Comic ID for CMS submission"
              sx={{ mt: 2, mb: 2 }}
            />
            <TextField
              margin="dense"
              id="episode-number"
              label="Episode Number"
              type="number"
              fullWidth
              variant="outlined"
              value={episodeNumber}
              onChange={(e) => setEpisodeNumber(e.target.value)}
              placeholder="Enter the Episode Number"
              sx={{ mb: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseComicIdDialog}>Cancel</Button>
            <Button 
              onClick={handleComicIdSubmit} 
              variant="contained"
              disabled={!comicId.trim() || !episodeNumber.trim()}
            >
              Submit to CMS
            </Button>
          </DialogActions>
        </Dialog>

        <DndProvider backend={HTML5Backend}>
          <CanvasPanels />
        </DndProvider>
      </>
    );
  }
};

export default CanvasDetail;

